import React from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { AiConsentCard } from '@/components/assistant/ai-consent-card';
import { AssistantChat } from '@/components/assistant/assistant-chat';
import { ThemedText } from '@/components/themed-text';
import { PageHeader } from '@/components/ui/page-header';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useAiConsent, useAssistantStatus, useClearAssistantThread } from '@/hooks/use-assistant';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { useToastStore } from '@/store/toastStore';

const PRIMARY = '#FD5001';

/**
 * A aba do assistente permanente (T-33).
 *
 * Fora do `PageLayout` de propósito: ele é um `ScrollView`, e um chat precisa
 * ocupar a altura toda e cuidar do próprio teclado. Mas o cabeçalho é o mesmo
 * `PageHeader asStrip` das telas de lista (serviços, contratos): aba raiz, sem
 * seta de voltar — só a faixa fixa com título/subtítulo e a conversa embaixo.
 *
 * Sem chave de IA no servidor, a tela não finge que existe conversa: diz o que
 * está acontecendo e devolve o prestador para as telas normais, que continuam
 * fazendo tudo o que o assistente faria.
 */
export default function AssistantScreen() {
  const status = useAssistantStatus();
  const clear = useClearAssistantThread();
  const consent = useAiConsent();
  const { addToast } = useToastStore();
  const plan = usePlanFeature('ASSISTANT');

  const unavailable = status.data && !status.data.agentAvailable;
  const needsConsent = !!status.data?.agentAvailable && !status.data.aiConsent;
  const goHome = () => router.replace('/(tabs)/(provider)/home');

  const revokeConsent = () =>
    consent.mutate(false, {
      onSuccess: () => addToast('Permissão da IA retirada.', 'success'),
      onError: () => addToast('Não deu para retirar a permissão.', 'error'),
    });

  return (
    <View className="flex-1 bg-canvas">
      <PageHeader
        title="Assistente"
        subtitle="Pergunte números, peça mudanças"
        asStrip
        action={
          plan.locked || needsConsent || unavailable ? undefined : (
            <View className="flex-row gap-2">
              <PressableScale
                onPress={revokeConsent}
                disabled={consent.isPending}
                accessibilityLabel="Retirar permissão da IA"
                className="h-9 w-9 items-center justify-center rounded-full bg-card"
                style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
              >
                <Ionicons className="text-neutral-500" name="shield-outline" size={17} />
              </PressableScale>
              <PressableScale
                onPress={() =>
                  clear.mutate(undefined, {
                    onError: () => addToast('Não deu para limpar a conversa.', 'error'),
                  })
                }
                disabled={clear.isPending}
                className="h-9 w-9 items-center justify-center rounded-full bg-card"
                style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
              >
                <Ionicons className="text-neutral-500" name="refresh-outline" size={17} />
              </PressableScale>
            </View>
          )
        }
      />

      <View className="min-h-0 flex-1">
        {plan.locked ? (
          <Locked />
        ) : unavailable ? (
          <Unavailable />
        ) : needsConsent ? (
          <AiConsentCard onDecline={goHome} declineLabel="Voltar para o início" />
        ) : !status.data ? null : (
          <AssistantChat brandColor={PRIMARY} onAgentDown={() => void status.refetch()} />
        )}
      </View>
    </View>
  );
}

/** Plano gratuito: a aba existe, e diz o que ganha quem assina. */
function Locked() {
  return (
    <View className="flex-1 items-center justify-center gap-3 px-8">
      <View className="h-14 w-14 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
        <Ionicons className="text-primary dark:text-white" name="sparkles-outline" size={28} />
      </View>
      <ThemedText className="text-center font-semibold text-base text-foreground">
        O assistente faz parte dos planos pagos
      </ThemedText>
      <ThemedText className="text-foreground/55 text-center text-sm">
        Com um plano, é só pedir: ele cria serviço, ajusta a agenda e responde sobre o seu
        negócio. No gratuito, tudo isso segue pelas telas de sempre.
      </ThemedText>
      <PressableScale
        onPress={() =>
          void navigateToSubscriptionPlans({ push: (path) => router.push(path as never) })
        }
        className="mt-2 rounded-full bg-primary px-5 py-2.5"
      >
        <ThemedText className="font-semibold text-sm text-white">Ver planos</ThemedText>
      </PressableScale>
    </View>
  );
}

function Unavailable() {
  return (
    <View className="flex-1 items-center justify-center gap-3 px-8">
      <Ionicons className="text-neutral-400" name="cloud-offline-outline" size={40} />
      <ThemedText className="text-center font-semibold text-base text-foreground">
        O assistente está fora do ar
      </ThemedText>
      <ThemedText className="text-foreground/55 text-center text-sm">
        Nada do seu negócio depende dele: serviços, agenda e cobranças continuam funcionando
        normalmente nas telas de sempre.
      </ThemedText>
      <PressableScale
        onPress={() => router.replace('/(tabs)/(provider)/home')}
        className="mt-2 rounded-full bg-primary px-5 py-2.5"
      >
        <ThemedText className="font-semibold text-sm text-white">Voltar para o início</ThemedText>
      </PressableScale>
    </View>
  );
}
